import React from 'react';
import { Box, LinearProgress, Typography, Tooltip, Chip } from '@mui/material';
import { Assessment, Info, CheckCircle, Warning, Error } from '@mui/icons-material';

interface CompetitionHealthIndicatorProps {
  competitionRate?: number;
  awardsWithData?: number;
  totalAwards?: number;
  soleSourceCount?: number;
  fullCompetitionCount?: number;
  showDetails?: boolean;
  variant?: 'compact' | 'detailed';
}

const CompetitionHealthIndicator: React.FC<CompetitionHealthIndicatorProps> = ({
  competitionRate = 0,
  awardsWithData = 0,
  totalAwards = 0,
  soleSourceCount = 0,
  fullCompetitionCount = 0,
  showDetails = true,
  variant = 'compact'
}) => {
  const percentage = competitionRate * 100;
  const hasData = awardsWithData > 0;
  
  const getColor = (): 'success' | 'warning' | 'error' | 'default' => {
    if (!hasData) return 'default';
    if (percentage >= 70) return 'success';
    if (percentage >= 40) return 'warning';
    return 'error';
  };

  const getHealthIcon = () => {
    if (!hasData) return <Info />;
    if (percentage >= 70) return <CheckCircle />;
    if (percentage >= 40) return <Warning />;
    return <Error />;
  };

  const getHealthLabel = () => {
    if (!hasData) return 'No Competition Data';
    if (percentage >= 70) return 'Healthy Competition';
    if (percentage >= 40) return 'Limited Competition';
    return 'Poor Competition';
  };

  const getTooltipContent = () => {
    if (!hasData) {
      return 'No competition data available for this vendor';
    }
    return `Competition Health: ${percentage.toFixed(1)}%
Full Competition: ${fullCompetitionCount} awards
Sole Source: ${soleSourceCount} awards
Data available for: ${awardsWithData} of ${totalAwards} awards`;
  };

  if (variant === 'compact') {
    return (
      <Tooltip title={getTooltipContent()} arrow>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            icon={getHealthIcon()}
            label={hasData ? `${percentage.toFixed(0)}% Competitive` : 'No Data'}
            color={getColor()}
            size="small"
            variant="outlined"
            sx={{ 
              '& .MuiChip-icon': { fontSize: 14 },
              fontSize: '0.75rem'
            }}
          />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Assessment sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Competition Health
          </Typography>
        </Box>
        <Tooltip title={getTooltipContent()} arrow>
          <Chip
            icon={getHealthIcon()}
            label={getHealthLabel()}
            color={getColor()}
            size="small"
            variant="filled"
            sx={{ '& .MuiChip-icon': { fontSize: 14 } }}
          />
        </Tooltip>
      </Box>
      
      <LinearProgress
        variant="determinate"
        value={hasData ? percentage : 0}
        color={hasData ? (percentage >= 70 ? 'success' : percentage >= 40 ? 'warning' : 'error') : 'inherit'}
        sx={{ 
          height: 8, 
          borderRadius: 4,
          backgroundColor: hasData ? undefined : 'grey.200'
        }}
      />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {hasData ? `${percentage.toFixed(1)}% competitive` : 'No data available'}
        </Typography>
        {showDetails && hasData && (
          <Typography variant="caption" color="text.secondary">
            {awardsWithData}/{totalAwards} awards with data
          </Typography>
        )}
      </Box>

      {showDetails && hasData && variant === 'detailed' && (
        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`${fullCompetitionCount} Full Competition`}
            size="small"
            color="success"
            variant="outlined"
          />
          <Chip
            label={`${soleSourceCount} Sole Source`}
            size="small"
            color="error"
            variant="outlined"
          />
          {(totalAwards - awardsWithData) > 0 && (
            <Chip
              label={`${totalAwards - awardsWithData} No Data`}
              size="small"
              color="default"
              variant="outlined"
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default CompetitionHealthIndicator;